<script setup lang="ts">
import {computed, nextTick, onBeforeUnmount, ref, watch} from 'vue'
import {useI18n} from 'vue-i18n'
import {useRoute} from 'vue-router'
import {autoUpdate, flip, offset, shift, useFloating} from '@floating-ui/vue'
import {useElementBounding} from '@vueuse/core'
import ButtonComponent from '@toolify/client/src/modules/ui/components/ButtonComponent.vue'
import {IconNameType} from '@toolify/server/src/enum/IconNameType'
import {IconWeightType} from '@toolify/client/src/modules/ui/enum/IconWeightType'
import {StyleVariantType} from '@toolify/client/src/modules/ui/enum/StyleVariantType'
import {ColorVariantType} from '@toolify/client/src/modules/ui/enum/ColorVariantType'
import {SizeVariantType} from '@toolify/client/src/modules/ui/enum/SizeVariantType'
import {ProductTourType} from '@toolify/server/src/enum/ProductTourType'
import {ProductTourStopReasonType} from '@toolify/client/src/modules/productTour/enum/ProductTourStopReasonType'
import {useResourceItemStore} from '@toolify/client/src/stores/ResourceItemStore/useResourceItemStore'
import {
  resolveProductTourStepAnchor,
} from '@toolify/client/src/modules/productTour/functions/resolveProductTourStepAnchor'
import {
  getEffectiveBackgroundColor,
} from '@toolify/client/src/modules/productTour/functions/getEffectiveBackgroundColor'
import {glueSingleLetterWords} from '@toolify/client/src/modules/productTour/functions/glueSingleLetterWords'
import {useProductTourStore} from '@toolify/client/src/stores/ProductTourStore/useProductTourStore'
import {useThemeVariables} from '@toolify/client/src/modules/theme/composables/useThemeVariables'
import {ThemeVariableType} from '@toolify/client/src/modules/theme/enum/ThemeVariableType'
import {useKeyboardShortcuts} from '@toolify/client/src/modules/keyboard/composables/useKeyboardShortcuts'
import {KeyboardSpecialKeyTypes} from '@toolify/client/src/stores/KeyboardShortcutStore/enum/KeyboardSpecialKeyTypes'

interface IBubbleAction {
  labelTranslationKey: string
  onClick: () => void
}

const {t} = useI18n()
const route = useRoute()
const productTourStore = useProductTourStore()
const resourceItemStore = useResourceItemStore()
const {getThemeVariable} = useThemeVariables()
const preferBorders = computed(() => getThemeVariable.value(ThemeVariableType.PreferBorders))

// Steps mentioning the product by name interpolate the deployment's branding
const stepTranslationParams = {appBrandingName: ENV.AppBrandingName}

const isMainTour = computed(() => productTourStore.activeTourType === ProductTourType.Main)
const isWorkspaceEmpty = computed(() => !resourceItemStore.hasAnyResource)

const anchorElement = ref<HTMLElement | null>(null)
const bubbleElement = ref<HTMLElement | null>(null)
const spotlightRingColor = ref<string | null>(null)

const currentStep = computed(() => productTourStore.currentStep)
const spotlightPadding = 8
const anchorBounding = useElementBounding(anchorElement)
// A zero-size bounding box (anchor hidden after being resolved) must fall back to
// the centered variant — otherwise the spotlight collapses into the viewport corner
const isCentered = computed(() => {
  return !currentStep.value?.anchor
    || !anchorElement.value
    || anchorBounding.width.value === 0
    || anchorBounding.height.value === 0
})

watch(currentStep, async (step) => {
  if (!step?.anchor) {
    anchorElement.value = null
    return
  }
  // The sidebar may have been opened by start() in the same tick — let it render first
  await nextTick()
  const element = resolveProductTourStepAnchor(step)
  element?.scrollIntoView({block: 'nearest'})
  anchorElement.value = element
  spotlightRingColor.value = element ? getEffectiveBackgroundColor(element) : null
}, {immediate: true})

// The anchors belong to the page the tour was started on — leaving it would
// strand a centered bubble with that page's copy over the next one
watch(() => route.path, () => {
  if (productTourStore.isActive) {
    productTourStore.stop(ProductTourStopReasonType.RouteChanged)
  }
})

const placement = computed(() => currentStep.value?.placement ?? 'right')

const {floatingStyles, x: bubbleX, y: bubbleY} = useFloating(anchorElement, bubbleElement, {
  placement,
  strategy: 'fixed',
  // The 44px offset leaves room for the connector arc between the target and the bubble
  middleware: [offset(44), flip(), shift({padding: 16})],
  whileElementsMounted: autoUpdate,
})

const bubbleBounding = useElementBounding(bubbleElement)

// A fallback anchor can cover most of the viewport (e.g. the whole canvas table) —
// no placement side has room then, so the bubble centers over the spotlight instead
const isBubbleCentered = computed(() => {
  if (isCentered.value) {
    return true
  }
  const anchorArea = anchorBounding.width.value * anchorBounding.height.value
  return anchorArea > window.innerWidth * window.innerHeight * 0.5
})
const spotlightStyle = computed(() => {
  return {
    top: `${anchorBounding.top.value - spotlightPadding}px`,
    left: `${anchorBounding.left.value - spotlightPadding}px`,
    width: `${anchorBounding.width.value + spotlightPadding * 2}px`,
    height: `${anchorBounding.height.value + spotlightPadding * 2}px`,
    '--spotlight-padding': `${spotlightPadding}px`,
    '--spotlight-ring-color': spotlightRingColor.value ?? 'transparent',
  }
})

// Point where the segment from a rect's center toward (directionX, directionY) crosses its edge
function getRectEdgePoint(
  centerX: number,
  centerY: number,
  halfWidth: number,
  halfHeight: number,
  directionX: number,
  directionY: number,
) {
  const scaleX = directionX === 0 ? Number.POSITIVE_INFINITY : halfWidth / Math.abs(directionX)
  const scaleY = directionY === 0 ? Number.POSITIVE_INFINITY : halfHeight / Math.abs(directionY)
  const scale = Math.min(scaleX, scaleY)
  return {
    x: centerX + directionX * scale,
    y: centerY + directionY * scale,
  }
}

const connectorGeometry = computed(() => {
  if (isBubbleCentered.value) {
    return null
  }
  if (
    anchorBounding.width.value === 0
    || bubbleBounding.width.value === 0
    || bubbleX.value == null
    || bubbleY.value == null
  ) {
    return null
  }
  const spotlightCenterX = anchorBounding.left.value + anchorBounding.width.value / 2
  const spotlightCenterY = anchorBounding.top.value + anchorBounding.height.value / 2
  const bubbleCenterX = bubbleX.value + bubbleBounding.width.value / 2
  const bubbleCenterY = bubbleY.value + bubbleBounding.height.value / 2
  const directionX = bubbleCenterX - spotlightCenterX
  const directionY = bubbleCenterY - spotlightCenterY
  if (directionX === 0 && directionY === 0) {
    return null
  }
  const start = getRectEdgePoint(
    spotlightCenterX,
    spotlightCenterY,
    anchorBounding.width.value / 2 + spotlightPadding,
    anchorBounding.height.value / 2 + spotlightPadding,
    directionX,
    directionY,
  )
  const end = getRectEdgePoint(
    bubbleCenterX,
    bubbleCenterY,
    bubbleBounding.width.value / 2,
    bubbleBounding.height.value / 2,
    -directionX,
    -directionY,
  )
  const distance = Math.hypot(end.x - start.x, end.y - start.y) || 1
  const arcOffset = Math.min(28, distance * 0.35)
  const controlX = (start.x + end.x) / 2 - ((end.y - start.y) / distance) * arcOffset
  const controlY = (start.y + end.y) / 2 + ((end.x - start.x) / distance) * arcOffset
  return {
    start,
    arcPath: `M ${start.x} ${start.y} Q ${controlX} ${controlY} ${end.x} ${end.y}`,
  }
})

// One stable pair of buttons whose label/handler follow the step — swapping
// button instances per step would unmount the focused control and drop keyboard
// focus to <body> on every "Next"
const offersCreateResourceFinale = computed(() => {
  return productTourStore.isLastStep && isMainTour.value && isWorkspaceEmpty.value
})

const secondaryAction = computed<IBubbleAction>(() => {
  if (productTourStore.isFirstStep) {
    return {
      labelTranslationKey: productTourStore.isAutoOffered ? 'productTour.actions.skipAll' : 'productTour.actions.skip',
      onClick: () => productTourStore.stop(ProductTourStopReasonType.Skipped),
    }
  }
  if (offersCreateResourceFinale.value) {
    return {
      labelTranslationKey: 'productTour.actions.finish',
      onClick: () => productTourStore.stop(ProductTourStopReasonType.Completed),
    }
  }
  return {
    labelTranslationKey: 'general.back',
    onClick: () => productTourStore.goToPreviousStep(),
  }
})

const primaryAction = computed<IBubbleAction>(() => {
  if (productTourStore.isFirstStep) {
    return {
      labelTranslationKey: isCentered.value ? 'productTour.actions.start' : 'general.next',
      onClick: () => productTourStore.goToNextStep(),
    }
  }
  if (offersCreateResourceFinale.value) {
    return {
      labelTranslationKey: 'productTour.actions.createResource',
      onClick: () => productTourStore.finishAndOpenCreateResource(),
    }
  }
  if (productTourStore.isLastStep) {
    return {
      labelTranslationKey: 'productTour.actions.finish',
      onClick: () => productTourStore.stop(ProductTourStopReasonType.Completed),
    }
  }
  return {
    labelTranslationKey: 'general.next',
    onClick: () => productTourStore.goToNextStep(),
  }
})

const showSkipRest = computed(() => !productTourStore.isFirstStep && !productTourStore.isLastStep)

function dismissTour() {
  productTourStore.stop(ProductTourStopReasonType.Dismissed)
}

// The page behind the bubble stays interactive (non-modal dialog), so focus is
// only moved into the bubble on open and handed back on close — Tab cycles inside
// the bubble solely while the focus is already in it
let lastFocusedElementBeforeTour: HTMLElement | null = null

watch(() => productTourStore.isActive, async (isActive) => {
  if (isActive) {
    lastFocusedElementBeforeTour = document.activeElement instanceof HTMLElement ? document.activeElement : null
    await nextTick()
    bubbleElement.value?.focus()
    return
  }
  if (lastFocusedElementBeforeTour?.isConnected) {
    lastFocusedElementBeforeTour.focus()
  }
  lastFocusedElementBeforeTour = null
}, {immediate: true})

const productTourActiveClass = 'is-product-tour-active'

watch(() => productTourStore.isActive, (isActive) => {
  document.documentElement.classList.toggle(productTourActiveClass, isActive)
}, {immediate: true})

onBeforeUnmount(() => {
  document.documentElement.classList.remove(productTourActiveClass)
})

const focusableSelector = 'button:not([disabled]), [href], input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])'

function onBubbleTabKeydown(event: KeyboardEvent) {
  const bubble = bubbleElement.value
  if (!bubble) {
    return
  }
  const focusableElements = [...bubble.querySelectorAll<HTMLElement>(focusableSelector)]
  if (!focusableElements.length) {
    event.preventDefault()
    bubble.focus()
    return
  }
  const firstElement = focusableElements[0]
  const lastElement = focusableElements[focusableElements.length - 1]
  const activeElement = document.activeElement
  if (event.shiftKey) {
    if (activeElement === firstElement || activeElement === bubble) {
      event.preventDefault()
      lastElement.focus()
    }
  } else if (activeElement === lastElement) {
    event.preventDefault()
    firstElement.focus()
  }
}

// Registered through the shortcut store so a modal opened mid-tour takes Escape
// first, and the arrows keep moving the caret while the user types in a field
// the step just pointed at
useKeyboardShortcuts([
  [[KeyboardSpecialKeyTypes.Escape], dismissTour],
  [[KeyboardSpecialKeyTypes.Right], () => productTourStore.goToNextStep()],
  [[KeyboardSpecialKeyTypes.Left], () => productTourStore.goToPreviousStep()],
], () => productTourStore.isActive, {skipWhileTyping: true})
</script>

<template>
  <Teleport to="body">
    <div
      class="product-tour-overlay"
      role="dialog"
      aria-labelledby="product-tour-bubble-title"
      aria-describedby="product-tour-bubble-description"
      v-if="productTourStore.isActive && currentStep"
    >
      <div class="tour-backdrop" v-if="isCentered"/>
      <div class="tour-spotlight" :style="spotlightStyle" v-else/>
      <svg class="tour-connector" v-if="connectorGeometry">
        <path class="connector-line" :d="connectorGeometry.arcPath"/>
        <circle
          class="connector-dot"
          :cx="connectorGeometry.start.x"
          :cy="connectorGeometry.start.y"
          r="4"
        />
      </svg>
      <div
        class="tour-bubble"
        ref="bubbleElement"
        tabindex="-1"
        :class="{'is-centered': isBubbleCentered, 'prefer-borders': preferBorders}"
        :style="isBubbleCentered ? undefined : floatingStyles"
        @keydown.tab="onBubbleTabKeydown"
      >
        <div class="bubble-header">
          <div class="bubble-title" id="product-tour-bubble-title" aria-live="polite">
            {{glueSingleLetterWords(t(currentStep.titleTranslationKey, stepTranslationParams))}}
          </div>
          <ButtonComponent
            :iconNameType="IconNameType.Xmark"
            :iconWeightType="IconWeightType.Solid"
            :style="StyleVariantType.ClearFilled"
            :color="ColorVariantType.Dark"
            :size="SizeVariantType.Medium"
            :aria-label="t('productTour.actions.close')"
            @click="dismissTour"
          />
        </div>
        <div class="bubble-description" id="product-tour-bubble-description">
          {{glueSingleLetterWords(t(currentStep.descriptionTranslationKey, stepTranslationParams))}}
        </div>
        <div class="bubble-footer">
          <div
            class="bubble-progress-dots"
            role="img"
            :aria-label="t('onboarding.stepper.label', {current: productTourStore.currentStepNumber, total: productTourStore.totalStepCount})"
          >
            <div
              class="progress-dot"
              v-for="stepNumber in productTourStore.totalStepCount"
              :key="stepNumber"
              :class="{
                'is-current': stepNumber === productTourStore.currentStepNumber,
                'is-done': stepNumber < productTourStore.currentStepNumber,
              }"
            />
          </div>
          <div class="bubble-actions">
            <ButtonComponent
              :style="StyleVariantType.ClearClear"
              :color="ColorVariantType.Dark"
              @click="dismissTour"
              v-if="showSkipRest"
            >
              {{t('productTour.actions.skipRest')}}
            </ButtonComponent>
            <ButtonComponent
              :style="StyleVariantType.ClearFilled"
              :color="ColorVariantType.Neutral"
              @click="secondaryAction.onClick()"
            >
              {{t(secondaryAction.labelTranslationKey)}}
            </ButtonComponent>
            <ButtonComponent @click="primaryAction.onClick()">
              {{t(primaryAction.labelTranslationKey)}}
            </ButtonComponent>
          </div>
        </div>
      </div>
    </div>
  </Teleport>
</template>

<style scoped lang="scss">
@use '@toolify/client/src/styles/variables' as variables;

.product-tour-overlay {
  position: fixed;
  inset: 0;
  // Below dialogs and the overlay stack (dropdowns, modals) so anything the user
  // opens from the still-clickable page renders above the scrim
  @include variables.zIndex(productTour);
  // The page behind the tour stays clickable — only the bubble captures events,
  // so controls like an active call's mute/hang-up are never blocked
  pointer-events: none;
}

.tour-backdrop {
  position: absolute;
  inset: 0;
  background: map-get(variables.$colors, modal-overlay);
  pointer-events: auto;
}

.tour-spotlight {
  position: fixed;
  box-sizing: border-box;
  border-radius: map-get(variables.$componentRadius, standard);
  border: 2px dashed map-get(variables.$colors, primary);
  box-shadow:
    0 0 0 200vmax map-get(variables.$colors, modal-overlay),
    inset 0 0 0 var(--spotlight-padding) var(--spotlight-ring-color);
  transition: top .25s ease, left .25s ease, width .25s ease, height .25s ease;
  pointer-events: none;

  @media (prefers-reduced-motion: reduce) {
    transition: none;
  }
}

.tour-connector {
  position: fixed;
  inset: 0;
  width: 100%;
  height: 100%;
  pointer-events: none;
}

.connector-line {
  stroke: map-get(variables.$colors, primary);
  stroke-width: 2;
  stroke-dasharray: 5 5;
  fill: none;
}

.connector-dot {
  fill: map-get(variables.$colors, primary);
}

.tour-bubble {
  pointer-events: auto;
  width: max-content;
  min-width: 22rem;
  max-width: min(28rem, calc(100vw - 2rem));
  box-sizing: border-box;
  // Programmatically focused container (tabindex="-1") — the ring belongs on the
  // inner controls, not on the dialog itself
  outline: none;
  background: map-get(variables.$colors, container);
  color: map-get(variables.$colors, content-normal);
  border: none;
  border-radius: map-get(variables.$componentRadius, large);
  padding: 1rem;
  display: grid;
  grid-template-columns: minmax(0, 1fr);
  gap: 0.75rem;

  &.prefer-borders {
    border: 1px solid map-get(variables.$colors, container-highest);
  }

  &.is-centered {
    position: absolute;
    left: 50%;
    top: 50%;
    transform: translate(-50%, -50%);
    min-width: 26rem;
  }
}

.bubble-header {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 0.5rem;
}

.bubble-title {
  min-width: 0;
  font-size: 1.25rem;
  font-weight: 600;
  color: map-get(variables.$colors, content-bright);
}

.bubble-description {
  font-size: 0.9375rem;
  line-height: 1.5;
  color: map-get(variables.$colors, content-normal);
}

.bubble-footer {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  justify-content: space-between;
  gap: 0.5rem;
}

.bubble-actions {
  display: flex;
  flex-wrap: wrap;
  justify-content: flex-end;
  gap: 0.5rem;
  margin-left: auto;
}

.bubble-progress-dots {
  display: flex;
  align-items: center;
  gap: 0.375rem;
}

.progress-dot {
  width: 0.375rem;
  height: 0.375rem;
  border-radius: 999px;
  background: map-get(variables.$colors, container-highest);
  transition: width .2s ease, background .2s ease;

  @media (prefers-reduced-motion: reduce) {
    transition: none;
  }

  &.is-done {
    background: map-get(variables.$colors, primary);
  }

  &.is-current {
    width: 1rem;
    background: map-get(variables.$colors, primary);
  }
}
</style>

<style lang="scss">
@use '@toolify/client/src/styles/variables' as variables;

html.is-product-tour-active {
  --scrollbar-rail-z-index: #{map-get(variables.$layerIndexes, productTour) - 1};
  --scrollbar-gradient-z-index: #{map-get(variables.$layerIndexes, productTour) - 2};

  #tooltip-divs {
    display: none;
  }
}
</style>
